import { REGISTRATION_PROFILE_JSON_KEY } from '@buxus/shared'
import {
  ABP_AUTH_TOKEN_STORAGE,
  ABP_BOOTSTRAP_STORAGE,
  ABP_USER_ID_STORAGE,
  BUXUS_API_BASE_URL,
  LOGOUT_URL,
  OPERATOR_SESSION_STORAGE_KEY,
  PROFILE_JURISDICTION_STORAGE_KEY,
  PROFILE_STAKEHOLDER_STORAGE_KEY,
} from '../abp/config'
import { idbClearOperatorPayloads } from '../buxus/operatorPayloadIdb'
import { idbClearOperatorFiles } from '../buxus/operatorFileIdb'
import { LOGIN_FORCE_PROMPT_STORAGE_KEY } from './loginErrorMessage'
import { clearProfileJurisdictionCountyStateKeys } from './profileJurisdictionStorage'
import { oidcManager } from './oidcManager'
import { resetAnalyticsSessionTracking } from '../analytics/analytics'

export type SessionTeardownHints = {
  accessToken?: string | null
  idToken?: string | null
}

/** Set during `purgeClientAuthStorage` so boot does not resurrect tokens from the OIDC store. */
export const AUTH_STORAGE_PURGED_KEY = 'buxus-web-auth-storage-purged-v1'

function ngrokSkipHeader(): Record<string, string> {
  if (BUXUS_API_BASE_URL.includes('ngrok')) {
    return { 'ngrok-skip-browser-warning': 'true' }
  }
  return {}
}

function scrubOidcLocalStorageKeys(): void {
  try {
    for (let i = localStorage.length - 1; i >= 0; i--) {
      const key = localStorage.key(i)
      if (key?.startsWith('oidc.')) localStorage.removeItem(key)
    }
  } catch {
    /* ignore */
  }
}

function clearOperatorSessionStorage(): void {
  try {
    localStorage.removeItem(OPERATOR_SESSION_STORAGE_KEY)
    for (let i = localStorage.length - 1; i >= 0; i--) {
      const key = localStorage.key(i)
      if (key?.startsWith(`${OPERATOR_SESSION_STORAGE_KEY}:`)) {
        localStorage.removeItem(key)
      }
    }
  } catch {
    /* ignore */
  }
}

/**
 * Remove all client-side auth material (ABP + OIDC + cached profile/operators).
 * Call after server logout and before `signoutRedirect` so a full page reload cannot re-hydrate the session.
 */
export async function purgeClientAuthStorage(): Promise<void> {
  localStorage.removeItem(ABP_AUTH_TOKEN_STORAGE)
  localStorage.removeItem(ABP_USER_ID_STORAGE)
  localStorage.removeItem(ABP_BOOTSTRAP_STORAGE)
  localStorage.removeItem(PROFILE_STAKEHOLDER_STORAGE_KEY)
  clearOperatorSessionStorage()
  try {
    localStorage.removeItem(REGISTRATION_PROFILE_JSON_KEY)
    localStorage.removeItem(PROFILE_JURISDICTION_STORAGE_KEY)
  } catch {
    /* ignore */
  }
  clearProfileJurisdictionCountyStateKeys()

  await oidcManager.removeUser().catch(() => {
    /* ignore */
  })
  scrubOidcLocalStorageKeys()

  void idbClearOperatorPayloads().catch(() => {
    /* ignore */
  })
  void idbClearOperatorFiles().catch(() => {
    /* ignore */
  })

  try {
    sessionStorage.setItem(AUTH_STORAGE_PURGED_KEY, '1')
  } catch {
    /* ignore */
  }
}

/** Best-effort ABP account logout while a bearer token is still available. */
export async function callAccountLogout(accessToken?: string | null): Promise<void> {
  const token = accessToken?.trim() || localStorage.getItem(ABP_AUTH_TOKEN_STORAGE)?.trim()
  if (!token) return
  try {
    await fetch(LOGOUT_URL, {
      method: 'GET',
      headers: {
        Accept: 'application/json',
        Authorization: `Bearer ${token}`,
        ...ngrokSkipHeader(),
      },
      credentials: 'omit',
    })
  } catch {
    /* non-fatal */
  }
}

function markForceLoginPrompt(): void {
  try {
    sessionStorage.setItem(LOGIN_FORCE_PROMPT_STORAGE_KEY, '1')
  } catch {
    /* ignore private mode */
  }
}

/**
 * End server + OIDC sessions so the next sign-in can use a different account.
 * Returns true when `signoutRedirect` started (page navigation in progress).
 */
export async function endOidcAndServerSession(hints?: SessionTeardownHints): Promise<boolean> {
  const oidcUser = await oidcManager.getUser().catch(() => null)
  const accessToken =
    hints?.accessToken?.trim() ||
    oidcUser?.access_token?.trim() ||
    localStorage.getItem(ABP_AUTH_TOKEN_STORAGE)?.trim() ||
    null
  const idToken = hints?.idToken ?? oidcUser?.id_token ?? null

  await callAccountLogout(accessToken)
  await purgeClientAuthStorage()
  resetAnalyticsSessionTracking()
  markForceLoginPrompt()

  try {
    await oidcManager.signoutRedirect({
      id_token_hint: idToken ?? undefined,
      post_logout_redirect_uri: window.location.origin,
    })
    return true
  } catch {
    return false
  }
}
